import { useEffect, useState } from "react";

import { oktaAuthApiRef, useApi } from "@backstage/core-plugin-api";
import { useTemplateSecrets } from "@backstage/plugin-scaffolder-react";
import {
    Defineable,
    Nullable,
} from "@internal/backstage-plugin-utility-common";

/**
 * Acquires an Okta access token and stores it in the scaffolder template secrets.
 *
 * @param options - Hook options.
 * @param options.secretsKey - The template secret key under which the token is stored.
 * @param options.onChange - Callback invoked with `"okta-token-set"` on success.
 * @returns An object containing `loading` and `error` state.
 */
export function useOktaToken({
    secretsKey,
    onChange,
}: {
    secretsKey: Defineable<string>;
    onChange: (value: string) => void;
}): { loading: boolean; error: Nullable<string> } {
    const oktaAuth = useApi(oktaAuthApiRef);
    const { setSecrets } = useTemplateSecrets();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Nullable<string>>(null);

    useEffect(() => {
        const fetchToken = async () => {
            try {
                if (!secretsKey) {
                    throw new Error(
                        "ui:options.secretsKey is required for OktaTokenField"
                    );
                }
                const token = await oktaAuth.getAccessToken();
                if (!token) {
                    throw new Error("Failed to get Okta access token");
                }
                setSecrets({ [secretsKey]: token });
                onChange("okta-token-set");
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setLoading(false);
            }
        };
        fetchToken();
    }, [oktaAuth, onChange, setSecrets, secretsKey]);

    return { loading, error };
}
